import { AbstractControl, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';

export interface ReportInvalidFormFieldsOptions {
  /** Translation key used for the toast title. */
  titleKey?: string;
  /** Whether to emit a `GlobalMessageUtil.warn` toast. Default: true. */
  toast?: boolean;
  /** Toast life in ms (forwarded to GlobalMessageUtil). Default: 5000. */
  life?: number;
}

/**
 * Iterates a strict-typed `FormGroup`, finds invalid controls, and (by default)
 * shows a warn toast listing each one with its translated label and error reason.
 * Returns the list of translated messages so callers can render them elsewhere
 * (e.g. an inline summary panel).
 *
 * @param form         Strict-typed FormGroup. Disabled controls are skipped.
 * @param fieldLabels  `Record<keyof T, string>` mapping control name → translation key.
 * @param translate    `TranslateService` for resolving labels and error messages.
 * @param options      See `ReportInvalidFormFieldsOptions`.
 *
 * @example
 *   if (this.form.invalid) {
 *     this.form.markAllAsTouched();
 *     reportInvalidFormFields(this.form, this.fieldLabels, this.translate);
 *     return;
 *   }
 */
export function reportInvalidFormFields<TControl extends { [K in keyof TControl]: AbstractControl<unknown> }>(
  form: FormGroup<TControl>,
  fieldLabels: Record<keyof TControl, string>,
  translate: TranslateService,
  options: ReportInvalidFormFieldsOptions = {},
): string[] {
  const { titleKey = 'Form_Validation_MissingFieldsTitle', toast = true, life = 5000 } = options;
  const messages: string[] = [];

  for (const key of Object.keys(form.controls) as (keyof TControl)[]) {
    const ctrl = form.controls[key] as AbstractControl;
    if (ctrl.disabled || !ctrl.invalid) continue;

    const label = translate.instant(fieldLabels[key]);
    const errorKey = errorTranslationKey(ctrl);
    messages.push(translate.instant(errorKey, { field: label }));
  }

  if (toast && messages.length > 0) {
    const title = translate.instant(titleKey);
    const body = messages.map((m) => `• ${m}`).join('\n');
    GlobalMessageUtil.warn(title, body, life);
  }

  return messages;
}

/** Maps Angular's built-in validator error keys to our translation keys. */
function errorTranslationKey(ctrl: AbstractControl): string {
  if (ctrl.hasError('required')) return 'Form_Validation_Required';
  if (ctrl.hasError('maxlength')) return 'Form_Validation_MaxLength';
  if (ctrl.hasError('minlength')) return 'Form_Validation_MinLength';
  if (ctrl.hasError('min')) return 'Form_Validation_Min';
  if (ctrl.hasError('max')) return 'Form_Validation_Max';
  if (ctrl.hasError('email')) return 'Form_Validation_Email';
  if (ctrl.hasError('pattern')) return 'Form_Validation_Pattern';
  return 'Form_Validation_Invalid';
}
